/* eslint-disable react/display-name */
import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import AlertDialog from '../components/AlertDialog';
import { TextInput } from '../components/Form';

const promptable =
  Component =>
  ({ dispose, reject, resolve, title, options = {} }) => {
    const [isOpen, setIsOpen] = useState(true);
    const [value, setValue] = useState('');

    const onDismiss = () => {
      setIsOpen(false);
      dispose();
    };

    const onCancel = () => {
      setIsOpen(false);
      reject(null);
    };

    const onProceed = () => {
      setIsOpen(false);
      resolve(value);
    };

    const handleChange = event => {
      setValue(event.target.value);
    };

    return (
      <Component
        title={title}
        options={{
          ...options,
          confirm: options.confirmText || 'OK',
          cancel: options.cancelText || 'Cancel',
        }}
        onCancel={onCancel}
        onDismiss={onDismiss}
        onProceed={onProceed}
        isOpen={isOpen}
        customContent={
          <TextInput
            autoFocus
            fullWidth
            value={value}
            onChange={handleChange}
            placeholder={options.placeholder}
          />
        }
      />
    );
  };

const createPrompt = (Component, mountingNode) => {
  return props => {
    const wrapper = (mountingNode || document.body).appendChild(document.createElement('div'));
    const root = createRoot(wrapper);

    const promise = new Promise((resolve, reject) => {
      try {
        root.render(<Component reject={reject} resolve={resolve} dispose={dispose} {...props} />);
      } catch (e) {
        console.error(e);
        throw e;
      }
    });

    function dispose() {
      root.unmount();
      setTimeout(() => {
        if (wrapper && wrapper.parentNode) {
          wrapper.parentNode.removeChild(wrapper);
        }
      });
    }

    return promise.then(
      result => {
        dispose();
        return result;
      },
      result => {
        dispose();
        return Promise.reject(result);
      },
    );
  };
};

const promptableDialog = promptable(AlertDialog);
const promptWrapper = createPrompt(promptableDialog);

// Wrapper function for easy usage
export function prompt(title, options = {}) {
  return promptWrapper({ title, options });
}
